import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { AppShell, PageHeader } from "../components/AppShell";
import BillingInfoDialog from "../components/BillingInfoDialog";
import { api, formatApiError } from "../lib/api";
import { useAuth } from "../lib/auth";
import { euro } from "../lib/constants";
import { toast } from "sonner";
import {
    CheckCircle2,
    XCircle,
    Clock,
    Crown,
    Sparkles,
    ShieldCheck,
    Zap,
    Star,
    Tag,
} from "lucide-react";

export default function Billing() {
    const { user, refresh } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [status, setStatus] = useState(null);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState("");
    const [error, setError] = useState("");
    const [resultMessage, setResultMessage] = useState(null);
    const [billingDialogOpen, setBillingDialogOpen] = useState(false);
    const [pendingPlanId, setPendingPlanId] = useState("");
    const polling = useRef(false);

    const loadStatus = async () => {
        try {
            const { data } = await api.get("/billing/status");
            setStatus(data);
        } catch (e) {
            setError(formatApiError(e));
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        loadStatus();
    }, []);

    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const sid = params.get("session_id");
        const canceled = params.get("canceled");
        if (canceled) {
            setResultMessage({ type: "warning", text: "Pagamento annullato. Nessun addebito effettuato." });
            window.history.replaceState(null, "", "/billing");
            return;
        }
        if (sid && !polling.current) {
            polling.current = true;
            setResultMessage({ type: "pending", text: "Verifica pagamento in corso…" });
            let attempts = 0;
            const maxAttempts = 8;
            const tick = async () => {
                attempts += 1;
                try {
                    const { data } = await api.get(`/billing/checkout/status/${sid}`);
                    if (data.payment_status === "paid") {
                        setResultMessage({
                            type: "success",
                            text: "Pagamento completato! Il tuo abbonamento è attivo per i prossimi 30 giorni.",
                        });
                        await loadStatus();
                        await refresh();
                        window.history.replaceState(null, "", "/billing");
                        return;
                    }
                    if (data.status === "expired") {
                        setResultMessage({ type: "warning", text: "Sessione scaduta. Riprova." });
                        window.history.replaceState(null, "", "/billing");
                        return;
                    }
                    if (attempts < maxAttempts) {
                        setTimeout(tick, 2000);
                    } else {
                        setResultMessage({
                            type: "warning",
                            text: "Verifica in corso. Controlla di nuovo tra qualche secondo.",
                        });
                    }
                } catch (e) {
                    setResultMessage({ type: "danger", text: formatApiError(e) });
                }
            };
            tick();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.search]);

    const choosePlan = async (planId) => {
        setError("");
        // Gate client-side: top-up non richiede billing info; piani sì
        const isTopup = planId && planId.startsWith("topup_");
        if (!isTopup && !user?.billing_info_complete) {
            setPendingPlanId(planId);
            setBillingDialogOpen(true);
            return;
        }
        setCreating(planId);
        try {
            // Niente più codice promo manuale: il backend applica automaticamente
            // la miglior promo pubblica attiva al checkout.
            const { data } = await api.post("/billing/checkout", {
                plan_id: planId,
                origin_url: window.location.origin,
            });
            window.location.href = data.url;
        } catch (e) {
            // Gate server-side: 412 → mancano dati di fatturazione
            if (e?.response?.status === 412) {
                setPendingPlanId(planId);
                setBillingDialogOpen(true);
                setCreating("");
                return;
            }
            setError(formatApiError(e));
            setCreating("");
        }
    };

    const handleBillingSaved = () => {
        // Riapre il checkout subito dopo aver salvato i dati di fatturazione
        if (pendingPlanId) {
            const p = pendingPlanId;
            setPendingPlanId("");
            setTimeout(() => choosePlan(p), 50);
        }
    };

    if (loading) {
        return (
            <AppShell>
                <div className="py-16 text-center text-sm text-brand-muted">Caricamento…</div>
            </AppShell>
        );
    }

    const basePlan = status.plans.find((p) => p.id === "monthly_base_29");
    const proPlan = status.plans.find((p) => p.id === "monthly_pro_49");
    const topUp = status.plans.find((p) => p.id === "topup_100_uploads");

    return (
        <AppShell>
            <PageHeader
                eyebrow="Piano & Abbonamento"
                title="Scegli il tuo piano"
                subtitle={`${status?.trial_days_total ?? 5} giorni di prova gratuita inclusi. Disdici quando vuoi.`}
            />

            {resultMessage && <ResultBanner msg={resultMessage} />}
            {error && (
                <div className="mb-5 rounded-lg bg-red-50 border border-red-200 text-brand-danger text-sm px-4 py-2.5" data-testid="billing-error">
                    {error}
                </div>
            )}

            {status?.public_promos?.length > 0 && (
                <PublicPromoBanner promos={status.public_promos} />
            )}

            <StatusCard status={status} />
            <QuotaCard quota={status?.quota} />
            <BillingInfoCard
                user={user}
                onEdit={() => setBillingDialogOpen(true)}
            />

            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-7 animate-fade-up">
                <PlanCard
                    plan={basePlan}
                    status={status}
                    onChoose={choosePlan}
                    creating={creating}
                    features={[
                        "253 ingredienti italiani inclusi",
                        "Catalogo 242 ricette pronte",
                        "Calcolo food cost in tempo reale",
                        "Acquisti con media ponderata",
                        "30 fatture AI al mese",
                        "3 caricamenti menu PDF al mese",
                        "10 ricette AI dettagliate al mese",
                        "Statistiche con grafici",
                        "Listino fornitori",
                    ]}
                    accent="#1a2a44"
                />
                <PlanCard
                    plan={proPlan}
                    status={status}
                    onChoose={choosePlan}
                    creating={creating}
                    highlight
                    features={[
                        "Tutto del piano Base",
                        "60 fatture AI al mese (2x)",
                        "5 caricamenti menu PDF al mese",
                        "15 ricette AI dettagliate al mese",
                        "Ordini fornitore intelligenti",
                        "Split automatico per miglior prezzo",
                        "Impasto pizza + forno legna/elettrico",
                        "Supporto prioritario",
                    ]}
                    accent="#c9a961"
                />
            </div>

            <AiCreditPacks user={user} />

            <TopUpCard plan={topUp} status={status} onChoose={choosePlan} creating={creating} />

            <section className="mt-12">
                <h2 className="font-display text-2xl text-brand-ink mb-4">Cosa include RENOVA360</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <Feature icon={<Sparkles size={18} />} title="Catalogo di 213 ricette pronte" text="Aggiungi al tuo menu i piatti italiani che preferisci con un click, food cost già calibrato." />
                    <Feature icon={<Zap size={18} />} title="Food cost calcolato live" text="Scarto, conversione unità, prezzo consigliato per ogni piatto in tempo reale." />
                    <Feature icon={<ShieldCheck size={18} />} title="Importazione fatture AI" text="Carica DDT e fatture: l'AI riconosce prodotti, quantità e prezzi e popola tutto." />
                    <Feature icon={<Star size={18} />} title="Ordini intelligenti (Pro)" text="Lascia al sistema scegliere il fornitore migliore per ogni voce e risparmia automaticamente." />
                </div>
            </section>

            <div className="mt-10 text-center">
                <button
                    className="text-sm text-brand-muted hover:text-brand-ink link-gold"
                    onClick={() => navigate("/profile")}
                    data-testid="billing-back-profile"
                >
                    Torna al profilo
                </button>
            </div>

            <BillingInfoDialog
                open={billingDialogOpen}
                onOpenChange={setBillingDialogOpen}
                onSaved={handleBillingSaved}
            />
        </AppShell>
    );
}

function StatusCard({ status }) {
    const endDate = useMemo(() => {
        if (!status) return null;
        const iso = status.current_period_end || status.trial_end;
        if (!iso) return null;
        try {
            return new Date(iso).toLocaleDateString("it-IT", {
                day: "2-digit", month: "long", year: "numeric",
            });
        } catch {
            return null;
        }
    }, [status]);
    if (!status) return null;
    const isActive = status.subscription_status === "active";
    const isTrial = status.subscription_status === "trialing";
    const tierLabel = status.plan_tier === "pro" ? "Pro" : "Base";
    const config = isActive
        ? {
              tint: "#2e7d32",
              badge: `Abbonato — ${tierLabel}`,
              icon: <Crown size={22} />,
              title: `Piano ${tierLabel} attivo`,
              subtitle: endDate ? `Prossimo rinnovo: ${endDate}` : "",
          }
        : isTrial
            ? {
                  tint: "#c9a961",
                  badge: `Prova gratuita — ${status.days_left} gg rimasti`,
                  icon: <Clock size={22} />,
                  title: `${status.days_left} giorni di prova rimanenti`,
                  subtitle: endDate ? `Termina il ${endDate}. Scegli un piano per continuare senza interruzioni.` : "",
              }
            : {
                  tint: "#d32f2f",
                  badge: "Scaduto",
                  icon: <XCircle size={22} />,
                  title: "Prova gratuita terminata",
                  subtitle: "Scegli un piano qui sotto per riattivare il tuo account.",
              };
    return (
        <div
            className="rounded-2xl p-6 sm:p-8 text-white shadow-card relative overflow-hidden"
            style={{ background: "linear-gradient(135deg, #1a2a44 0%, #243a5e 55%, #2a4268 100%)" }}
            data-testid="billing-status-card"
        >
            <div className="flex items-start gap-4">
                <div
                    className="h-12 w-12 rounded-full flex items-center justify-center shrink-0"
                    style={{ background: `${config.tint}22`, color: config.tint }}
                >
                    {config.icon}
                </div>
                <div className="flex-1">
                    <div
                        className="inline-flex items-center gap-2 rounded-full px-3 py-1 text-[10px] uppercase tracking-[0.16em] font-bold"
                        style={{ background: `${config.tint}22`, color: config.tint }}
                        data-testid="billing-badge"
                    >
                        <span className="h-1.5 w-1.5 rounded-full" style={{ background: config.tint }} />
                        {config.badge}
                    </div>
                    <h3 className="mt-3 font-display text-3xl leading-tight">{config.title}</h3>
                    {config.subtitle && (
                        <p className="mt-2 text-sm text-white/75">{config.subtitle}</p>
                    )}
                </div>
            </div>
        </div>
    );
}

function PlanCard({ plan, status, onChoose, creating, features, highlight, accent }) {
    if (!plan) return null;
    const isCurrent = status.subscription_status === "active" && status.plan_tier === plan.tier;
    const isLoading = creating === plan.id;
    const anyLoading = !!creating;
    const promo = plan.auto_promo;
    return (
        <div
            data-testid={`billing-plan-${plan.tier}`}
            className={`rounded-2xl bg-paper border shadow-card p-6 sm:p-7 relative overflow-hidden ${
                highlight ? "border-brand-gold/60 ring-1 ring-brand-gold/30" : "border-brand-line"
            }`}
        >
            {highlight && (
                <div className="absolute top-0 right-0 px-3 py-1 rounded-bl-xl bg-brand-gold text-brand-navy text-[10px] uppercase tracking-[0.18em] font-bold">
                    Consigliato
                </div>
            )}
            <div className="eyebrow" style={{ color: accent }}>{plan.tier === "pro" ? "Piano Pro" : "Piano Base"}</div>
            {promo ? (
                <div className="mt-2">
                    <div className="flex items-baseline gap-2 flex-wrap">
                        <div className="font-display text-3xl text-brand-muted leading-none line-through decoration-2 decoration-brand-danger/60">
                            {euro(plan.amount)}
                        </div>
                        <div
                            className="font-display text-5xl text-emerald-700 leading-none"
                            data-testid={`billing-discounted-${plan.tier}`}
                        >
                            {euro(promo.discounted_amount)}
                        </div>
                        <div className="text-sm text-brand-muted">/ mese</div>
                    </div>
                    <div className="mt-2 flex items-center gap-2 flex-wrap">
                        <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-bold px-2 py-0.5">
                            <Tag size={11} /> {promo.code} · -{promo.discount_pct}%
                        </span>
                        <span className="text-[11px] text-brand-muted">
                            Risparmi {euro(promo.savings)}
                        </span>
                    </div>
                </div>
            ) : (
                <div className="mt-2 flex items-baseline gap-2">
                    <div className="font-display text-5xl text-brand-ink leading-none">{euro(plan.amount)}</div>
                    <div className="text-sm text-brand-muted">/ mese</div>
                </div>
            )}
            <div className="mt-2 text-sm text-brand-muted">
                {plan.tier === "pro" ? "Piano Pro" : "Piano Base"} · {euro(plan.amount)}/mese
            </div>

            <ul className="mt-5 space-y-2.5">
                {features.map((f) => (
                    <li key={f} className="flex items-start gap-2.5 text-sm text-brand-ink">
                        <CheckCircle2 size={16} className="text-brand-success mt-0.5 shrink-0" />
                        <span>{f}</span>
                    </li>
                ))}
            </ul>

            <button
                onClick={() => onChoose(plan.id)}
                disabled={anyLoading || isCurrent}
                data-testid={`billing-choose-${plan.tier}`}
                className={`mt-6 w-full rounded-full py-3 text-sm font-semibold transition disabled:opacity-60 ${
                    highlight
                        ? "bg-brand-gold text-brand-navy hover:bg-brand-goldDark shadow-gold"
                        : "bg-brand-navy text-white hover:bg-brand-navyLight"
                }`}
            >
                {isCurrent
                    ? "Piano attivo"
                    : isLoading
                        ? "Apertura Stripe…"
                        : promo
                            ? `Acquista a ${euro(promo.discounted_amount)}`
                            : `Scegli ${plan.tier === "pro" ? "Pro" : "Base"}`}
            </button>
            <div className="mt-3 text-[11px] text-brand-muted text-center">
                Pagamento sicuro gestito da Stripe.
            </div>
        </div>
    );
}

function Feature({ icon, title, text }) {
    return (
        <div className="bg-paper rounded-xl border border-brand-line shadow-card p-5 flex gap-4">
            <div className="h-9 w-9 rounded-full bg-brand-gold/15 text-brand-goldDark flex items-center justify-center shrink-0">
                {icon}
            </div>
            <div>
                <div className="font-display text-lg text-brand-ink leading-tight">{title}</div>
                <div className="mt-1 text-sm text-brand-muted">{text}</div>
            </div>
        </div>
    );
}

function ResultBanner({ msg }) {
    const map = {
        success: { bg: "rgba(46,125,50,0.08)", border: "#2e7d32", color: "#2e7d32", icon: <CheckCircle2 size={16} /> },
        warning: { bg: "rgba(237,108,2,0.08)", border: "#ed6c02", color: "#ed6c02", icon: <Clock size={16} /> },
        danger: { bg: "rgba(211,47,47,0.08)", border: "#d32f2f", color: "#d32f2f", icon: <XCircle size={16} /> },
        pending: { bg: "rgba(2,136,209,0.08)", border: "#0288d1", color: "#0288d1", icon: <Clock size={16} /> },
    };
    const s = map[msg.type] || map.pending;
    return (
        <div
            className="mb-6 rounded-xl px-5 py-3.5 flex items-center gap-3 border"
            style={{ background: s.bg, borderColor: s.border, color: s.color }}
            data-testid="billing-result-banner"
        >
            {s.icon}
            <span className="text-sm font-semibold">{msg.text}</span>
        </div>
    );
}

function QuotaCard({ quota }) {
    if (!quota) return null;
    const plan = (quota.tier_for_quota || "base").toUpperCase();
    const rows = [
        {
            key: "invoices",
            label: "Fatture AI importate",
            used: quota.invoices_used,
            total: quota.plan_invoices,
            extra: quota.extra_invoices,
        },
        {
            key: "menus",
            label: "Caricamenti menu PDF",
            used: quota.menus_used,
            total: quota.plan_menus,
            extra: quota.extra_menus,
        },
        {
            key: "recipes",
            label: "Ricette AI generate",
            used: quota.recipes_used,
            total: quota.plan_recipes,
            extra: quota.extra_recipes,
        },
        {
            key: "magic",
            label: "Magic Menu generati",
            used: quota.magic_used || 0,
            total: quota.plan_magic || 0,
            extra: quota.extra_magic || 0,
        },
    ];
    return (
        <div
            className="mt-6 bg-paper rounded-2xl border border-brand-line shadow-card overflow-hidden"
            data-testid="quota-card"
        >
            <div className="px-5 py-4 border-b border-brand-line flex items-center justify-between gap-3">
                <div>
                    <div className="eyebrow text-brand-gold">Quote upload del mese</div>
                    <div className="font-display text-lg text-brand-ink mt-0.5">
                        Piano {plan} · periodo {quota.period}
                    </div>
                </div>
            </div>
            <div className="divide-y divide-brand-line">
                {rows.map((r) => {
                    const pct = r.total > 0 ? Math.min(100, (r.used / r.total) * 100) : 0;
                    const left = Math.max(0, r.total - r.used) + r.extra;
                    const exhausted = left === 0;
                    return (
                        <div key={r.key} className="px-5 py-4" data-testid={`quota-row-${r.key}`}>
                            <div className="flex items-center justify-between text-[13px] mb-1.5">
                                <span className="text-brand-ink font-semibold">{r.label}</span>
                                <span
                                    className={exhausted ? "text-brand-danger font-bold" : "text-brand-muted"}
                                >
                                    {r.used} / {r.total} piano
                                    {r.extra > 0 && (
                                        <span className="ml-2 text-brand-gold font-bold">
                                            + {r.extra} extra
                                        </span>
                                    )}
                                </span>
                            </div>
                            <div className="h-1.5 rounded-full bg-brand-line/60 overflow-hidden">
                                <div
                                    className={
                                        "h-full rounded-full transition-all duration-500 " +
                                        (exhausted
                                            ? "bg-brand-danger"
                                            : pct > 80
                                            ? "bg-brand-gold"
                                            : "bg-brand-navy")
                                    }
                                    style={{ width: `${pct}%` }}
                                />
                            </div>
                            <div className="mt-1.5 text-[11px] text-brand-muted">
                                {exhausted
                                    ? "Quota esaurita — acquista la ricarica da 10€ qui sotto."
                                    : `${left} rimanenti questo mese`}
                            </div>
                        </div>
                    );
                })}
            </div>
        </div>
    );
}

function TopUpCard({ plan, status, onChoose, creating }) {
    if (!plan) return null;
    const disabled = !status?.has_active_access;
    const promo = plan.auto_promo;
    const finalAmount = promo ? promo.discounted_amount : plan.amount;
    return (
        <div
            className="mt-8 rounded-2xl border-2 border-brand-gold/40 bg-gradient-to-br from-brand-gold/10 to-paper p-6 animate-fade-up"
            data-testid="topup-card"
        >
            <div className="flex items-start justify-between gap-6 flex-wrap">
                <div className="min-w-0 flex-1">
                    <div className="eyebrow text-brand-gold flex items-center gap-1.5">
                        <Zap size={11} /> Ricarica upload
                    </div>
                    <h3 className="font-display text-3xl text-brand-ink mt-1">
                        100 upload extra + 10 Magic Menu ·{" "}
                        {promo ? (
                            <>
                                <span className="line-through decoration-2 decoration-brand-danger/60 text-brand-muted text-2xl">
                                    {euro(plan.amount)}
                                </span>{" "}
                                <span className="text-emerald-700" data-testid="topup-discounted">
                                    {euro(finalAmount)}
                                </span>
                            </>
                        ) : (
                            <>{euro(plan.amount)}</>
                        )}
                    </h3>
                    {promo && (
                        <div className="mt-1 flex items-center gap-2 flex-wrap">
                            <span className="inline-flex items-center gap-1 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-700 text-[11px] font-bold px-2 py-0.5">
                                <Tag size={11} /> {promo.code} · -{promo.discount_pct}%
                            </span>
                            <span className="text-[11px] text-brand-muted">
                                Risparmi {euro(promo.savings)}
                            </span>
                        </div>
                    )}
                    <p className="mt-2 text-sm text-brand-ink/80 max-w-xl">
                        Hai finito le quote del mese? Aggiungi{" "}
                        <strong>80 fatture AI</strong> +{" "}
                        <strong>5 caricamenti menu PDF</strong> +{" "}
                        <strong>15 ricette AI</strong> +{" "}
                        <strong>10 Magic Menu</strong> al tuo account. I crediti
                        extra non scadono a fine mese e si consumano solo quando
                        le quote del piano sono finite.
                    </p>
                </div>
                <button
                    type="button"
                    onClick={() => onChoose(plan.id)}
                    disabled={disabled || creating === plan.id}
                    data-testid="topup-buy-btn"
                    className="inline-flex items-center gap-2 rounded-full bg-brand-gold text-brand-navy px-6 py-3 text-sm font-bold hover:bg-brand-goldDark disabled:opacity-50 transition shadow-gold shrink-0"
                >
                    <Sparkles size={14} />
                    {creating === plan.id
                        ? "Redirect…"
                        : `Acquista a ${euro(finalAmount)}`}
                </button>
            </div>
        </div>
    );
}



// ---------------------------------------------------------------------------
// PUBLIC PROMO BANNER — appare se ci sono promo `public:true` attive
// ---------------------------------------------------------------------------
function PublicPromoBanner({ promos }) {
    const [idx, setIdx] = useState(0);
    useEffect(() => {
        if (promos.length <= 1) return;
        const t = setInterval(() => setIdx((i) => (i + 1) % promos.length), 6000);
        return () => clearInterval(t);
    }, [promos.length]);
    if (!promos.length) return null;
    const p = promos[idx];
    const expires = p.valid_until ? new Date(p.valid_until) : null;
    return (
        <div
            className="mb-6 rounded-2xl bg-gradient-to-r from-brand-navy via-brand-navyLight to-brand-navy text-white px-5 sm:px-7 py-5 flex flex-col sm:flex-row items-start sm:items-center gap-4 shadow-card border border-brand-gold/40 animate-fade-up"
            data-testid="public-promo-banner"
        >
            <div className="h-12 w-12 rounded-full bg-brand-gold text-brand-navy flex items-center justify-center shrink-0 shadow-gold animate-pulse">
                <Sparkles size={20} />
            </div>
            <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                    <span className="text-[10px] uppercase tracking-[0.2em] font-bold text-brand-gold">
                        Offerta limitata · sconto già applicato
                    </span>
                    {expires && (
                        <span className="text-[10px] uppercase tracking-wider font-bold text-white/60 inline-flex items-center gap-1">
                            <Clock size={11} /> fino al {expires.toLocaleString("it-IT", { day: "2-digit", month: "short" })}
                        </span>
                    )}
                </div>
                <div className="font-display text-2xl sm:text-3xl mt-1 leading-tight">
                    {p.description || `Sconto ${p.discount_pct}%`}
                </div>
                <div className="mt-2 text-[13px] text-white/85">
                    Lo sconto è automatico al checkout
                    {p.applies_to !== "all" && (
                        <> · valido solo sul piano <strong>{p.applies_to === "pro" ? "Pro" : p.applies_to === "base" ? "Base" : "Top-up"}</strong></>
                    )}
                    .
                </div>
                {p.notes && (
                    <div
                        className="mt-2 text-[12px] text-white/70 italic leading-relaxed whitespace-pre-line border-l-2 border-brand-gold/40 pl-3"
                        data-testid={`promo-notes-${p.code}`}
                    >
                        {p.notes}
                    </div>
                )}
            </div>
            {promos.length > 1 && (
                <div className="flex gap-1 self-end sm:self-center">
                    {promos.map((_, i) => (
                        <button
                            key={i}
                            onClick={() => setIdx(i)}
                            className={`h-1.5 rounded-full transition-all ${i === idx ? "w-6 bg-brand-gold" : "w-3 bg-white/30"}`}
                            aria-label={`Promo ${i + 1}`}
                        />
                    ))}
                </div>
            )}
        </div>
    );
}


function BillingInfoCard({ user, onEdit }) {
    const complete = !!user?.billing_info_complete;
    const rows = [
        ["Ragione sociale / Nome e Cognome", user?.billing_company_name],
        ["P.IVA / Codice Fiscale", user?.billing_vat_or_cf],
        ["Indirizzo", user?.billing_address],
        ["Email fatturazione", user?.billing_email],
        ["PEC / SDI", user?.billing_pec_or_sdi],
        ["Telefono", user?.billing_phone],
    ];
    return (
        <div
            className={`mt-5 rounded-xl border p-5 ${
                complete
                    ? "bg-paper border-brand-line"
                    : "bg-amber-50 border-amber-300"
            }`}
            data-testid="billing-info-card"
        >
            <div className="flex items-center justify-between gap-3 flex-wrap mb-3">
                <div>
                    <div className="eyebrow text-brand-ink/70 mb-1">
                        Dati di fatturazione
                    </div>
                    <div
                        className={`text-sm font-bold ${
                            complete ? "text-brand-ink" : "text-amber-800"
                        }`}
                    >
                        {complete
                            ? "Tutti i dati sono compilati"
                            : "Inserisci i dati prima di passare al piano a pagamento"}
                    </div>
                </div>
                <button
                    type="button"
                    onClick={onEdit}
                    data-testid="billing-info-edit"
                    className="inline-flex items-center gap-1.5 rounded-full bg-brand-navy text-white px-4 py-2 text-[12px] font-bold hover:bg-brand-navyLight transition"
                >
                    {complete ? "Modifica" : "Compila ora"}
                </button>
            </div>
            {complete && (
                <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-1.5 text-[12px]">
                    {rows.map(([k, v]) => (
                        <div key={k} className="flex justify-between gap-3">
                            <dt className="text-brand-muted truncate">{k}</dt>
                            <dd className="text-brand-ink font-semibold text-right truncate max-w-[60%]">
                                {v || "—"}
                            </dd>
                        </div>
                    ))}
                </dl>
            )}
        </div>
    );
}

function AiCreditPacks({ user }) {
    const [balance, setBalance] = useState(null);
    const [busy, setBusy] = useState(null);
    useEffect(() => {
        api.get("/ai-credits/balance").then(({ data }) => setBalance(data)).catch(() => null);
    }, []);
    const buy = async (packId) => {
        setBusy(packId);
        try {
            const origin = window.location.origin;
            const { data } = await api.post("/ai-credits/checkout", {
                pack_id: packId,
                success_url: `${origin}/billing?ai_credits=success`,
                cancel_url: `${origin}/billing?ai_credits=cancel`,
            });
            if (data.url) window.location.href = data.url;
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(null);
        }
    };
    if (!balance) return null;
    return (
        <section className="mt-10 animate-fade-up" data-testid="ai-credits-section">
            <div className="bg-paper rounded-2xl border border-brand-line shadow-card p-6 sm:p-8">
                <div className="flex flex-wrap items-end justify-between gap-4 mb-5">
                    <div>
                        <div className="text-[11px] uppercase tracking-wider font-bold text-brand-gold mb-1">
                            Crediti AI universali
                        </div>
                        <h2 className="font-display text-2xl text-brand-ink">Ricarica i tuoi crediti</h2>
                        <p className="text-sm text-brand-muted mt-1">
                            1 credito = 1 utilizzo AI (1 pagina PDF, 1 menu Magic Chef, 1 calcolo Magic Bartender, 1 fattura).
                        </p>
                    </div>
                    <div className="text-right">
                        <div className="text-[11px] uppercase tracking-wider font-bold text-brand-muted">
                            Saldo attuale
                        </div>
                        <div className="font-display text-3xl text-brand-ink" data-testid="ai-credits-balance">
                            {balance.unlimited_ai ? "∞" : balance.ai_credits}
                        </div>
                    </div>
                </div>

                <div className="bg-brand-cream rounded-lg p-4 mb-5 text-[12px] leading-relaxed text-brand-ink">
                    <strong className="text-brand-navy">Costi per azione:</strong>{" "}
                    Magic Chef <strong>1 credito</strong> · OCR fattura <strong>1 per pagina</strong> · Listino bevande PDF <strong>1 per pagina</strong> · Magic Bartender <strong>1 credito</strong>{" "}
                    · Hard limit upload: <strong>max 2 pagine PDF per volta</strong>.
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3">
                    {balance.packs.map((p, idx) => (
                        <button
                            key={p.id}
                            onClick={() => buy(p.id)}
                            disabled={busy === p.id || balance.unlimited_ai}
                            data-testid={`ai-pack-${p.id}`}
                            className={`relative p-5 rounded-xl border-2 transition text-left disabled:opacity-50 disabled:cursor-not-allowed ${
                                idx === 1
                                    ? "border-brand-gold bg-brand-gold/5 hover:bg-brand-gold/10"
                                    : "border-brand-line bg-white hover:border-brand-navy"
                            }`}
                        >
                            {idx === 1 && (
                                <span className="absolute -top-2 right-3 bg-brand-gold text-white text-[9px] uppercase tracking-wider font-bold px-2 py-0.5 rounded-full">
                                    Più scelto
                                </span>
                            )}
                            <div className="font-display text-3xl text-brand-ink mb-1">
                                {p.credits}
                            </div>
                            <div className="text-[10px] uppercase tracking-wider font-bold text-brand-muted mb-2">
                                crediti AI
                            </div>
                            <div className="text-lg font-bold text-brand-ink">
                                €{p.price_eur.toFixed(2)}
                            </div>
                            <div className="text-[10px] text-brand-muted">
                                €{(p.price_eur / p.credits).toFixed(2)} per credito
                            </div>
                            {busy === p.id && (
                                <div className="mt-2 text-[10px] text-brand-muted">Apertura checkout…</div>
                            )}
                        </button>
                    ))}
                </div>
            </div>
        </section>
    );
}
